import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import Skeleton from '@mui/material/Skeleton'
import IconButton from '@mui/material/IconButton'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { adminGetEvent, adminCreateEvent, adminUpdateEvent } from '../../api/admin'
import { STATUS_VALUES } from '../../constants'
import { SectionCard, Txt, SwitchRow, SelectRow, StringList, PeopleEditor, ContactsEditor } from './fields'
import RichTextViewer from '../Events/RichTextViewer'

const EMPTY_EVENT = {
  title: '',
  slug: '',
  subtitle: '',
  description: '',
  status: 'later',
  published: true,
  registrationsClosed: false,
  regCloseHoursBeforeStart: 12,
  date: [],
  startTime: '',
  beThereBefore: '',
  duration: '',
  showMedia: true,
  organizedBy: [],
  hostedBy: [],
  contacts: [],
  banners: [],
  socialShareBanner: '',
  splashBanner: '',
  earlyBirdPrice: '',
  earlyBirdEndDateTime: '',
  earlyBirdPaymentLink: '',
  discountedPrice: '',
  discountedPriceEndDateTime: '',
  regularPrice: '',
  regularPaymentLink: '',
  reserveNotice: { bring: [], important: '' },
  location: { name: '', note: '', gpsLL: '', googleMaps: '', waze: '' },
  details: ''
}

function slugify (str) {
  return String(str || '').toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function toDateInput (value) {
  if (!value) return ''
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  if (typeof value === 'string') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? value : d.toISOString().slice(0, 10)
  }
  return ''
}

export default function EventForm () {
  const { slug } = useParams()
  const navigate = useNavigate()
  const isEdit = !!slug
  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue
  } = useForm({ defaultValues: EMPTY_EVENT })

  const title = watch('title')
  const dateArray = useFieldArray({ control, name: 'date' })

  useEffect(() => {
    if (!isEdit) {
      reset(EMPTY_EVENT)
      setLoading(false)
      return
    }

    let active = true
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const event = await adminGetEvent(slug)
        if (!event) {
          if (active) setError('Event not found')
          return
        }
        if (active) {
          reset({
            ...EMPTY_EVENT,
            ...event,
            date: Array.isArray(event.date) ? event.date.map(toDateInput) : []
          })
        }
      } catch (err) {
        if (active) setError(err.message)
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => { active = false }
  }, [isEdit, slug, reset])

  const onSubmit = async (values) => {
    setSaving(true)
    setError(null)
    try {
      if (isEdit) {
        await adminUpdateEvent(slug, values)
      } else {
        const created = await adminCreateEvent(values)
        navigate(`/admin/dashboard/events/${created.slug}/edit`, { replace: true })
        return
      }
      navigate('/admin/dashboard/events')
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <Skeleton variant='rectangular' height={600} />
  }

  const tabs = ['Basics', 'People', 'Media', 'Pricing', 'Location', 'Details']

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h5' component='h1'>
          {isEdit ? `Edit Event — ${slug}` : 'New Event'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={() => navigate('/admin/dashboard/events')}>Cancel</Button>
          <Button type='submit' variant='contained' disabled={saving}>
            {saving ? 'Saving…' : 'Save Event'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        {tabs.map(t => <Tab key={t} label={t} />)}
      </Tabs>

      {activeTab === 0 && (
        <SectionCard title='Basics'>
          <Txt label='Title' value={watch('title')} onChange={v => setValue('title', v)} />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Txt label='Slug' value={watch('slug')} onChange={v => setValue('slug', slugify(v))} />
            </Box>
            <Button size='small' startIcon={<AutoAwesomeIcon />} sx={{ mt: 1 }} onClick={() => setValue('slug', slugify(title))}>
              Auto-slugify
            </Button>
          </Box>
          <Txt label='Subtitle' value={watch('subtitle')} multiline onChange={v => setValue('subtitle', v)} />
          <Txt label='Description' value={watch('description')} multiline minRows={3} onChange={v => setValue('description', v)} />
          <SelectRow label='Status' value={watch('status')} onChange={v => setValue('status', v)} options={STATUS_VALUES} />
          <SwitchRow label='Published' value={watch('published')} onChange={v => setValue('published', v)} />
          <SwitchRow label='Registrations closed' value={watch('registrationsClosed')} onChange={v => setValue('registrationsClosed', v)} />
          <Txt label='Reg close hours before start' type='number' value={watch('regCloseHoursBeforeStart')} onChange={v => setValue('regCloseHoursBeforeStart', v)} />
          <Box>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>Event dates</Typography>
            {dateArray.fields.map((field, i) => (
              <Box key={field.id} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  type='date'
                  size='small'
                  fullWidth
                  value={watch(`date.${i}`) || ''}
                  onChange={e => setValue(`date.${i}`, e.target.value)}
                />
                <IconButton size='small' disabled={dateArray.fields.length <= 1} onClick={() => dateArray.remove(i)}><DeleteIcon fontSize='small' /></IconButton>
              </Box>
            ))}
            <Button size='small' startIcon={<AddIcon />} onClick={() => dateArray.append('')}>Add date</Button>
          </Box>
          <Txt label='Start time' value={watch('startTime')} onChange={v => setValue('startTime', v)} />
          <Txt label='Be there before (minutes)' value={watch('beThereBefore')} onChange={v => setValue('beThereBefore', v)} />
          <Txt label='Duration (minutes)' value={watch('duration')} onChange={v => setValue('duration', v)} />
          <SwitchRow label='Show media' value={watch('showMedia')} onChange={v => setValue('showMedia', v)} />
        </SectionCard>
      )}

      {activeTab === 1 && (
        <Stack spacing={2}>
          <SectionCard title='Organized By'>
            <Controller
              control={control}
              name='organizedBy'
              render={({ field }) => <PeopleEditor label='Organizers' value={field.value} onChange={field.onChange} />}
            />
          </SectionCard>
          <SectionCard title='Hosted By'>
            <Controller
              control={control}
              name='hostedBy'
              render={({ field }) => <PeopleEditor label='Hosts' value={field.value} onChange={field.onChange} showContact />}
            />
          </SectionCard>
          <SectionCard title='Contacts'>
            <Controller
              control={control}
              name='contacts'
              render={({ field }) => <ContactsEditor label='Contacts' value={field.value} onChange={field.onChange} />}
            />
          </SectionCard>
        </Stack>
      )}

      {activeTab === 2 && (
        <SectionCard title='Media'>
          <Controller
            control={control}
            name='banners'
            render={({ field }) => <StringList label='Banners' value={field.value} onChange={field.onChange} />}
          />
          <Txt label='Social share banner' value={watch('socialShareBanner')} onChange={v => setValue('socialShareBanner', v)} />
          <Txt label='Splash image (hero)' value={watch('splashBanner')} onChange={v => setValue('splashBanner', v)} />
        </SectionCard>
      )}

      {activeTab === 3 && (
        <SectionCard title='Pricing'>
          <Txt label='Early bird price (cents)' value={watch('earlyBirdPrice')} onChange={v => setValue('earlyBirdPrice', v)} />
          <Txt label='Early bird end date/time' value={watch('earlyBirdEndDateTime')} onChange={v => setValue('earlyBirdEndDateTime', v)} />
          <Txt label='Early bird payment link (Stripe)' value={watch('earlyBirdPaymentLink')} onChange={v => setValue('earlyBirdPaymentLink', v)} />
          <Txt label='Discounted price (cents)' value={watch('discountedPrice')} onChange={v => setValue('discountedPrice', v)} />
          <Txt label='Discounted price end date/time' value={watch('discountedPriceEndDateTime')} onChange={v => setValue('discountedPriceEndDateTime', v)} />
          <Txt label='Regular price (cents)' value={watch('regularPrice')} onChange={v => setValue('regularPrice', v)} />
          <Txt label='Regular payment link (Stripe)' value={watch('regularPaymentLink')} onChange={v => setValue('regularPaymentLink', v)} />
          <Typography variant='subtitle2' sx={{ mt: 1 }}>Reserve confirmation popup</Typography>
          <StringList label='What I should bring' value={watch('reserveNotice.bring')} onChange={v => setValue('reserveNotice.bring', v)} placeholder='Item' />
          <Txt label='Important information' value={watch('reserveNotice.important')} multiline minRows={3} onChange={v => setValue('reserveNotice.important', v)} />
        </SectionCard>
      )}

      {activeTab === 4 && (
        <SectionCard title='Location'>
          <Txt label='Name' value={watch('location.name')} onChange={v => setValue('location.name', v)} />
          <Txt label='Note' value={watch('location.note')} multiline minRows={2} onChange={v => setValue('location.note', v)} />
          <Txt label='GPS (lat,lng)' value={watch('location.gpsLL')} onChange={v => setValue('location.gpsLL', v)} />
          <Txt label='Google Maps' value={watch('location.googleMaps')} onChange={v => setValue('location.googleMaps', v)} />
          <Txt label='Waze' value={watch('location.waze')} onChange={v => setValue('location.waze', v)} />
        </SectionCard>
      )}

      {activeTab === 5 && (
        <SectionCard title='Details'>
          <Typography variant='body2' sx={{ mb: 2, color: 'text.secondary' }}>
            Edit the rich content below, then press Save in the editor and Save Event to persist.
          </Typography>
          <RichTextViewer
            content={watch('details')}
            isEditing
            onSave={serialized => setValue('details', serialized)}
          />
        </SectionCard>
      )}
    </Box>
  )
}
